import { formatOrdinal, renderPlural, Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import type { PerformanceAchievementQuarter } from "../../api/entities/performancePlaybook.js";
import { AnimatedProgressBar } from "./animatedProgressBar.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";

type QuarterlyTierName =
  | "Consistent"
  | "Exceptional"
  | "Elite"
  | "Legendary"
  | "Hall of Fame";
type QuarterlyTierAccent = "green" | "bronze" | "silver" | "gold" | "platinum";

interface QuarterlyTier {
  percentileFloor: number;
  tier: QuarterlyTierName;
  reward: string;
}

const quarterlyTiers: readonly QuarterlyTier[] = [
  {
    percentileFloor: 50,
    tier: "Consistent",
    reward: "🎁 Unlock a profile badge.",
  },
  {
    percentileFloor: 75,
    tier: "Exceptional",
    reward: "🎁 Unlock a rare badge.",
  },
  {
    percentileFloor: 90,
    tier: "Elite",
    reward: "🎁 Ring the Alpharun gong for your team to see.",
  },
  {
    percentileFloor: 97,
    tier: "Legendary",
    reward: "🎁 Get exclusive Alpharun swag.",
  },
  {
    percentileFloor: 99,
    tier: "Hall of Fame",
    reward: "🎁 Receive the iconic golden Alpharun trophy.",
  },
];

const quarterlyTierAccentByTier: Record<
  QuarterlyTierName,
  QuarterlyTierAccent
> = {
  Consistent: "green",
  Exceptional: "bronze",
  Elite: "silver",
  Legendary: "gold",
  "Hall of Fame": "platinum",
};

const quarterlyTierAccentTextStyles: Record<QuarterlyTierAccent, string> = {
  green: "text-emerald-700",
  bronze: "text-orange-700",
  silver: "text-muted-foreground",
  gold: "text-amber-600",
  platinum: "text-yellow-900",
};

interface RunwaySegment {
  readonly realStart: number;
  readonly realEnd: number;
  readonly displayWidth: number;
  readonly tier: QuarterlyTierName;
  readonly gradient: string;
}

/**
 * The bar starts at the 50th-percentile threshold (Consistent).
 * Display widths are non-linear so elite tiers get enough visual space (sum = 100).
 */
const runwaySegments: readonly RunwaySegment[] = [
  {
    realStart: 50,
    realEnd: 75,
    displayWidth: 26,
    tier: "Consistent",
    gradient: "from-emerald-400 to-green-500",
  },
  {
    realStart: 75,
    realEnd: 90,
    displayWidth: 26,
    tier: "Exceptional",
    gradient: "from-amber-400 to-yellow-600",
  },
  {
    realStart: 90,
    realEnd: 97,
    displayWidth: 22,
    tier: "Elite",
    gradient: "from-gray-300 to-slate-400",
  },
  {
    realStart: 97,
    realEnd: 99,
    displayWidth: 15,
    tier: "Legendary",
    gradient: "from-yellow-300 via-amber-400 to-yellow-300",
  },
  {
    realStart: 99,
    realEnd: 100,
    displayWidth: 11,
    tier: "Hall of Fame",
    gradient: "from-cyan-200 via-white to-sky-300",
  },
];

/** Map a real runway position (0–100) to a display position. Below 50 → 0. */
function mapToDisplayPosition(realPosition: number): number {
  if (realPosition <= 50) return 0;
  let displayOffset = 0;
  for (const seg of runwaySegments) {
    if (realPosition <= seg.realEnd) {
      const t = (realPosition - seg.realStart) / (seg.realEnd - seg.realStart);
      return displayOffset + t * seg.displayWidth;
    }
    displayOffset += seg.displayWidth;
  }
  return 100;
}

/** Pre-compute display positions for the tier threshold markers. */
const tierRunwayMarkers = quarterlyTiers.map((tier) => {
  const realPosition = tier.percentileFloor;
  return { displayPosition: mapToDisplayPosition(realPosition), tier };
});

function getCurrentQuarterlyTier(
  percentile: number,
): QuarterlyTier | undefined {
  const reachedTiers = quarterlyTiers.filter(
    (tier) => tier.percentileFloor <= percentile,
  );
  if (reachedTiers.length === 0) {
    return undefined;
  }
  return reachedTiers.reduce((currentBest, tier) =>
    currentBest.percentileFloor < tier.percentileFloor ? tier : currentBest,
  );
}

interface AchievementsLeaderboardProps {
  selectedQuarterData: PerformanceAchievementQuarter | undefined;
}

export const AchievementsLeaderboard: FC<AchievementsLeaderboardProps> = ({
  selectedQuarterData,
}) => {
  const playbookPercentileRank =
    selectedQuarterData?.achievements.playbook_adherence_percentile ?? null;
  const callCount = selectedQuarterData?.achievements.nb_calls ?? 0;
  const qualifyingCallCount = selectedQuarterData?.nb_qualifying_calls ?? 0;
  const qualifiedTeammateCount =
    selectedQuarterData?.nb_qualified_teammates ?? 0;

  const currentQuarterlyTier = useMemo(
    () =>
      playbookPercentileRank != null
        ? getCurrentQuarterlyTier(playbookPercentileRank)
        : undefined,
    [playbookPercentileRank],
  );

  const nextTierOnRunway = useMemo(() => {
    if (playbookPercentileRank == null) return undefined;
    if (!currentQuarterlyTier) {
      return quarterlyTiers[0];
    }
    const currentIdx = quarterlyTiers.findIndex(
      (t) => t.percentileFloor === currentQuarterlyTier.percentileFloor,
    );
    return quarterlyTiers[currentIdx + 1];
  }, [playbookPercentileRank, currentQuarterlyTier]);

  const daysLeftInQuarter = useMemo(() => {
    if (!selectedQuarterData) return null;
    const now = DateTime.now();
    const startDate = DateTime.fromISO(selectedQuarterData.start_date).startOf(
      "day",
    );
    const endDate = DateTime.fromISO(selectedQuarterData.end_date).endOf("day");
    if (!startDate.isValid || !endDate.isValid) return null;
    if (now > endDate || now < startDate) return null;
    return Math.max(1, Math.ceil(endDate.diff(now, "days").days));
  }, [selectedQuarterData]);

  const runwayPosition =
    playbookPercentileRank != null ? playbookPercentileRank : 0;
  // If the highest tier is unlocked, fill the bar completely.
  const displayPosition =
    currentQuarterlyTier && !nextTierOnRunway
      ? 100
      : mapToDisplayPosition(runwayPosition);

  const findAdherenceScoreForTier = (
    tier: QuarterlyTier,
  ): number | undefined => {
    const percentiles =
      selectedQuarterData?.team_playbook_adherence_percentiles;
    if (!percentiles) {
      return undefined;
    }

    for (const { p, v } of percentiles) {
      if (tier.percentileFloor <= p) {
        return v;
      }
    }

    return undefined;
  };

  return (
    <div className="rounded-md bg-white px-5 pt-3 pb-5 shadow-sm ring-1 ring-black/5">
      <div className="mt-3 flex items-end gap-4">
        {callCount >= qualifyingCallCount ? (
          <div>
            <span className="text-3xl font-semibold text-foreground">
              {playbookPercentileRank != null
                ? `${formatOrdinal(Math.floor(playbookPercentileRank))} percentile`
                : "—"}
            </span>
            <span className="ml-1.5 text-sm text-muted-foreground">
              among {qualifiedTeammateCount} team members
            </span>
          </div>
        ) : (
          <div>
            <span className="text-3xl font-semibold text-foreground">
              Not placed
            </span>
            <span className="ml-1.5 text-sm text-muted-foreground">
              {renderPlural(qualifyingCallCount, {
                one: <>{callCount} out of 1 call</>,
                other: (n) => (
                  <>
                    {callCount} out of {n.toLocaleString()} calls
                  </>
                ),
              })}
            </span>
          </div>
        )}
        <div className="flex items-end gap-1.5 border-l border-border-light pl-4">
          <TooltipTrigger>
            <TooltipHoverTarget className="cursor-default" shape="rounded">
              <GradingPassRatePill
                size={Size.SMALL}
                passRate={
                  selectedQuarterData?.achievements.playbook_adherence_score ??
                  undefined
                }
              />
            </TooltipHoverTarget>
            <SimpleTooltip>
              Simple average playbook adherence score of all calls this quarter.
            </SimpleTooltip>
          </TooltipTrigger>
          <span className="text-sm text-muted-foreground">average score</span>
        </div>
        {daysLeftInQuarter != null && selectedQuarterData ? (
          <div className="ml-auto flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 ring-1 ring-amber-200">
            <span className="text-sm font-semibold text-amber-700">
              {daysLeftInQuarter}
            </span>
            <span className="text-xs text-amber-600">
              {daysLeftInQuarter === 1 ? "day" : "days"} left in Q
              {DateTime.fromISO(selectedQuarterData.start_date).quarter}
            </span>
          </div>
        ) : null}
      </div>

      {/* Gap callout */}
      {callCount < qualifyingCallCount ? (
        <p className="mt-2 text-sm text-foreground">
          {renderPlural(qualifyingCallCount - callCount, {
            one: (
              <>
                <span className="font-semibold">1</span> more call to qualify
              </>
            ),
            other: (n) => (
              <>
                <span className="font-semibold">{n.toLocaleString()}</span> more
                calls to qualify
              </>
            ),
          })}
        </p>
      ) : playbookPercentileRank != null && nextTierOnRunway ? (
        <div className="mt-2 text-sm text-foreground">
          Reach a score of{" "}
          <GradingPassRatePill
            size={Size.SMALL}
            passRate={findAdherenceScoreForTier(nextTierOnRunway) ?? undefined}
          />{" "}
          to unlock the{" "}
          <span className="font-semibold">{nextTierOnRunway.tier}</span> tier
          this quarter.
        </div>
      ) : playbookPercentileRank != null &&
        currentQuarterlyTier &&
        !nextTierOnRunway ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Highest tier unlocked. Keep it up!
        </p>
      ) : null}

      <div className="relative mt-4">
        <AnimatedProgressBar
          displayPosition={displayPosition}
          showGlow={playbookPercentileRank != null}
        >
          <div className="absolute inset-0 flex">
            {runwaySegments.map((seg, i) => (
              <div
                key={seg.realStart}
                className={twMerge(
                  "h-full bg-gradient-to-r",
                  seg.gradient,
                  i > 0 && "border-l border-white/40",
                )}
                style={{ width: `${seg.displayWidth}%` }}
              />
            ))}
          </div>
        </AnimatedProgressBar>

        {/* Tier threshold markers below track */}
        <div className="relative mt-2 flex">
          {tierRunwayMarkers.map(({ tier }) => {
            const realPos = tier.percentileFloor;
            const isPassed =
              playbookPercentileRank != null && runwayPosition >= realPos;
            const seg = runwaySegments.find((s) => s.tier === tier.tier);
            const accent = quarterlyTierAccentByTier[tier.tier];

            return (
              <div
                key={tier.tier}
                className="group relative"
                style={{ width: `${seg?.displayWidth ?? 0}%` }}
              >
                {/* Label */}
                <div
                  className={twMerge(
                    "mt-1 text-xs leading-tight",
                    isPassed
                      ? twMerge(
                          "font-semibold",
                          quarterlyTierAccentTextStyles[accent],
                        )
                      : "text-gray-400",
                  )}
                >
                  <div>{tier.tier}</div>
                  <div
                    className={twMerge(
                      "mt-0.5 text-[10px]",
                      isPassed ? "opacity-70" : "",
                    )}
                  >
                    Top {100 - tier.percentileFloor}%
                  </div>
                </div>

                {/* Tooltip on hover */}
                <div className="pointer-events-none absolute top-full left-0 z-30 mt-1 rounded-lg bg-gray-900 px-3 py-2 opacity-0 shadow-xl transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
                  <div className="absolute bottom-full left-3 border-4 border-transparent border-b-gray-900" />
                  <div className="text-xs font-semibold whitespace-nowrap text-white">
                    {tier.tier}
                  </div>
                  <div className="mt-0.5 text-[11px] whitespace-nowrap text-gray-300">
                    {tier.reward}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
